import { getSupportedLanguageCodes, normalizeLanguageCode } from '../i18n/languages.js';
import { LEGAL_PAGE_DEFINITIONS } from './legalPageManifest.js';

const LEGAL_MARKDOWN_PATH_RE = /^(?:(?<locale>[^/]+)\/)?(?<sourceFile>[^/]+)\.md$/;
const LEGAL_CONTENT_ROOT_MARKER = '/content/legal/';

function normalizePath(value) {
  return String(value || '').replace(/\\/g, '/');
}

function toRelativeLegalMarkdownPath(filePath) {
  const normalizedPath = normalizePath(filePath);
  const rootMarkerIndex = normalizedPath.lastIndexOf(LEGAL_CONTENT_ROOT_MARKER);
  if (rootMarkerIndex >= 0) {
    return normalizedPath.slice(rootMarkerIndex + LEGAL_CONTENT_ROOT_MARKER.length);
  }

  return normalizedPath
    .replace(/^\.\//, '')
    .replace(/^\.\.\//, '')
    .replace(/^content\/legal\//, '');
}

function dedupeInOrder(values) {
  const uniqueValues = [];
  const seen = new Set();

  for (const value of values) {
    if (seen.has(value)) {
      continue;
    }

    seen.add(value);
    uniqueValues.push(value);
  }

  return uniqueValues;
}

function buildTargetKey(slug, locale) {
  return `${slug}:${locale}`;
}

export function getShippedPublicLegalPageDefinitions(pageDefinitions = LEGAL_PAGE_DEFINITIONS) {
  return pageDefinitions.map((entry) => Object.freeze({ ...entry }));
}

export function getShippedPublicLegalPageSlugs(pageDefinitions = LEGAL_PAGE_DEFINITIONS) {
  return getShippedPublicLegalPageDefinitions(pageDefinitions).map((entry) => entry.slug);
}

export function getSupportedLegalLocaleCodes(supportedLanguageCodes = getSupportedLanguageCodes()) {
  const normalizedCodes = dedupeInOrder(
    supportedLanguageCodes
      .map((code) => normalizeLanguageCode(code))
      .filter(Boolean),
  );

  if (normalizedCodes.includes('en')) {
    return Object.freeze(normalizedCodes);
  }

  return Object.freeze(['en', ...normalizedCodes]);
}

export function parseLegalMarkdownPath(filePath) {
  const normalizedPath = normalizePath(filePath);
  const relativePath = toRelativeLegalMarkdownPath(normalizedPath);
  const match = relativePath.match(LEGAL_MARKDOWN_PATH_RE);
  if (!match?.groups?.sourceFile) {
    return null;
  }

  const hasLocaleDirectory = Boolean(match.groups.locale);
  const rawLocale = hasLocaleDirectory ? match.groups.locale : 'en';
  const locale = hasLocaleDirectory ? normalizeLanguageCode(rawLocale) : 'en';

  return Object.freeze({
    filePath: normalizedPath,
    relativePath,
    sourceFile: match.groups.sourceFile,
    rawLocale,
    locale,
    hasLocaleDirectory,
    isDefaultLocaleFile: !hasLocaleDirectory || rawLocale === 'en',
    isCanonicalLocaleDirectory: !hasLocaleDirectory || rawLocale === locale,
  });
}

export function collectLegalSourceCoverage(options = {}) {
  const {
    contentPaths = [],
    pageDefinitions = LEGAL_PAGE_DEFINITIONS,
    supportedLanguageCodes = getSupportedLanguageCodes(),
  } = options;

  const publicPages = getShippedPublicLegalPageDefinitions(pageDefinitions);
  const pageSlugs = publicPages.map((page) => page.slug);
  const supportedLocales = getSupportedLegalLocaleCodes(supportedLanguageCodes);
  const supportedLocaleSet = new Set(supportedLocales);
  const pageBySourceFile = new Map(publicPages.map((page) => [page.sourceFile, page]));
  const discoveredSourceTargets = new Map();
  const invalidLocalePathEntries = [];
  const unsupportedLocalePathEntries = [];
  const unmappedSourcePathEntries = [];

  for (const contentPath of contentPaths) {
    const parsedPath = parseLegalMarkdownPath(contentPath);
    if (!parsedPath) {
      continue;
    }

    if (parsedPath.hasLocaleDirectory && !parsedPath.isCanonicalLocaleDirectory) {
      invalidLocalePathEntries.push(parsedPath);
      continue;
    }

    if (parsedPath.hasLocaleDirectory && !supportedLocaleSet.has(parsedPath.locale)) {
      unsupportedLocalePathEntries.push(parsedPath);
      continue;
    }

    const page = pageBySourceFile.get(parsedPath.sourceFile);
    if (!page) {
      unmappedSourcePathEntries.push(parsedPath);
      continue;
    }

    const targetKey = buildTargetKey(page.slug, parsedPath.locale);
    const existingTarget = discoveredSourceTargets.get(targetKey);
    if (existingTarget) {
      existingTarget.filePaths.push(parsedPath.filePath);
      continue;
    }

    discoveredSourceTargets.set(targetKey, {
      slug: page.slug,
      locale: parsedPath.locale,
      kind: page.kind,
      path: page.path,
      sourceFile: page.sourceFile,
      filePaths: [parsedPath.filePath],
    });
  }

  const discoveredLocales = dedupeInOrder([
    'en',
    ...[...discoveredSourceTargets.values()]
      .map((target) => target.locale)
      .filter((locale) => locale !== 'en'),
  ]);

  const renderedTargets = [];
  const rawSourceTargets = [];
  const structuredSourceTargets = [];
  const missingSourceTargets = [];
  const duplicateSourceTargets = [];

  for (const page of publicPages) {
    for (const locale of supportedLocales) {
      renderedTargets.push(Object.freeze({
        slug: page.slug,
        locale,
        kind: page.kind,
        path: page.path,
        sourceFile: page.sourceFile,
      }));

      const targetKey = buildTargetKey(page.slug, locale);
      const discoveredTarget = discoveredSourceTargets.get(targetKey) || null;
      const rawTarget = Object.freeze({
        slug: page.slug,
        locale,
        kind: page.kind,
        path: page.path,
        sourceFile: page.sourceFile,
        filePath: discoveredTarget?.filePaths?.[0] || null,
        filePaths: Object.freeze([...(discoveredTarget?.filePaths || [])]),
      });

      rawSourceTargets.push(rawTarget);
      if (page.kind === 'structured') {
        structuredSourceTargets.push(rawTarget);
      }

      if (!discoveredTarget) {
        missingSourceTargets.push(rawTarget);
        continue;
      }

      if (discoveredTarget.filePaths.length > 1) {
        duplicateSourceTargets.push(rawTarget);
      }
    }
  }

  return Object.freeze({
    publicPages: Object.freeze(publicPages),
    pageSlugs: Object.freeze(pageSlugs),
    supportedLocales,
    scannedLocales: Object.freeze(discoveredLocales),
    strictLocales: Object.freeze([...supportedLocales]),
    renderedTargets: Object.freeze(renderedTargets),
    rawSourceTargets: Object.freeze(rawSourceTargets),
    structuredSourceTargets: Object.freeze(structuredSourceTargets),
    missingSourceTargets: Object.freeze(missingSourceTargets),
    duplicateSourceTargets: Object.freeze(duplicateSourceTargets),
    invalidLocalePathEntries: Object.freeze(invalidLocalePathEntries),
    unsupportedLocalePathEntries: Object.freeze(unsupportedLocalePathEntries),
    unmappedSourcePathEntries: Object.freeze(unmappedSourcePathEntries),
  });
}
